import { Language } from '../resources/interfaces';
import {
  Article,
  Event,
  GetArticlesParams,
  MeResponse,
  Module,
  PaginatedResponse,
  PostModuleOrdersParams,
  Tag,
  TagIdList,
  TagLinkList,
  UserTag,
  LoginResponse,
  NotificationIds,
  Notification,
  ArticleResponse,
  GetSimilarArticlesParams,
  OnboardedUser,
  GetAdminArticlesParams,
  AdminArticleRead,
  GetEventsParams,
  AdminPostReadDetail,
  AdminArticleCreate,
  GetAdminExtremeEventsParams,
  AdminExtremeEventRead,
  ExtremeEvent,
  EventResponse,
  GetSimilarEventsParams,
  AdminExtremeEventCreate,
  AdminEventCreate,
  GetAdminEventsParams,
  AdminEventRead,
  UploadFileResponse,
  CalendarEvent,
  FileModuleType,
  BirthdayDetail,
  GetHelpdeskIssuesSearchParams,
  HelpdeskIssueSearchResult,
  Topic,
  Organization,
  GetAdminHelpdeskCategoriesParams,
  AdminTopicRead,
  AdminTopicCreate,
  AdminTopicUpdate,
  AdminTopicReadDetail,
  AdminOrganizationRead,
  AdminOrganizationCreate,
  AdminOrganizationUpdate,
  AdminOrganizationReadDetail,
  CategoriesStatusFilter,
  GetAdminHelpdeskIssuesParams,
  AdminHelpdeskIssueRead,
  AdminPostRead,
  HelpdeskIssueStatusFilter,
  AdminHelpdeskIssueCreate,
  TopicActivity,
} from './interfaces';
import { axiosInstance, iamAxiosInstance } from './axiosInstance';
import { ENDPOINTS, IAM_BE_ENDPOINTS } from './endpoints';

// const INCREASED_TIMEOUT = 60 * 1000;

class Client {
  logout = async (): Promise<boolean> => {
    const response = await iamAxiosInstance.post(IAM_BE_ENDPOINTS.LOGOUT);
    return response.data;
  };

  refreshToken = async (): Promise<LoginResponse> => {
    const response = await iamAxiosInstance.post(IAM_BE_ENDPOINTS.REFRESH_TOKEN);
    return response.data;
  };

  getArticles = async (params: GetArticlesParams): Promise<PaginatedResponse<Article>> => {
    const response = await axiosInstance.get(`${ENDPOINTS.ARTICLES}`, {
      params,
    });

    return response.data;
  };

  getEvents = async (params: GetEventsParams): Promise<PaginatedResponse<Event>> => {
    const response = await axiosInstance.get(`${ENDPOINTS.EVENTS}`, {
      params,
    });

    return response.data;
  };

  getExtremeEventUnseen = async (): Promise<ExtremeEvent[]> => {
    const response = await axiosInstance.get(`${ENDPOINTS.EXTREME_EVENT_UNSEEN}`);
    return response.data;
  };

  createExtremeEventSeen = async (extremeEventId: number): Promise<void> => {
    await axiosInstance.post(`${ENDPOINTS.EXTREME_EVENT_SEEN}/${extremeEventId}`);
  };

  deleteExtremeEventSeen = async (postId: number): Promise<void> => {
    await axiosInstance.delete(`${ENDPOINTS.EXTREME_EVENT_SEEN}/${postId}`);
  };

  getHighlightedArticles = async (): Promise<Article[]> => {
    const response = await axiosInstance.get(`${ENDPOINTS.HIGHLIGHTED_ARTICLES}`);

    return response.data;
  };

  getArticle = async (postId: number): Promise<ArticleResponse> => {
    const response = await axiosInstance.get(`${ENDPOINTS.POSTS}/${postId}`);
    return response.data;
  };

  getEvent = async (postId: number): Promise<EventResponse> => {
    const response = await axiosInstance.get(`${ENDPOINTS.POSTS}/${postId}`);
    return response.data;
  };

  getSimilarArticles = async (params: GetSimilarArticlesParams): Promise<Article[]> => {
    const response = await axiosInstance.get(ENDPOINTS.SIMILAR_ARTICLES, { params });
    return response.data;
  };

  getSimilarEvents = async (params: GetSimilarEventsParams): Promise<Event[]> => {
    const response = await axiosInstance.get(ENDPOINTS.SIMILAR_EVENTS, { params });
    return response.data;
  };

  getAllTags = async (): Promise<Tag[]> => {
    const response = await axiosInstance.get(`${ENDPOINTS.TAGS}`);
    return response.data;
  };

  getUserTags = async (): Promise<UserTag[]> => {
    const response = await axiosInstance.get(`${ENDPOINTS.TAGS}/user`);
    return response.data;
  };

  getNotifications = async (): Promise<Notification[]> => {
    const response = await axiosInstance.get(`${ENDPOINTS.NOTIFICATIONS}`);
    return response.data;
  };
  setNotificationsSeen = async (notificationIds: NotificationIds): Promise<void> => {
    await axiosInstance.post(`${ENDPOINTS.NOTIFICATIONS_SEEN}`, notificationIds);
  };

  createPostBookmark = async (postId: number): Promise<void> => {
    await axiosInstance.post(`${ENDPOINTS.BOOKMARKED}/${postId}`);
  };

  deletePostBookmark = async (postId: number): Promise<void> => {
    await axiosInstance.delete(`${ENDPOINTS.BOOKMARKED}/${postId}`);
  };

  deleteTag = async (tagIds: TagIdList): Promise<void> => {
    await axiosInstance.post(`${ENDPOINTS.TAG_DELETE}`, tagIds);
  };

  createTag = async (params: TagLinkList): Promise<void> => {
    await axiosInstance.post(`${ENDPOINTS.TAGS}`, params);
  };

  getModules = async (): Promise<Module[]> => {
    const response = await axiosInstance.get(`${ENDPOINTS.MODULES}`);
    return response.data;
  };

  changeModulesOrder = async (params: PostModuleOrdersParams): Promise<void> => {
    await axiosInstance.post(`${ENDPOINTS.MODULES_ORDER}`, params);
  };

  getMe = async (): Promise<MeResponse> => {
    const response = await axiosInstance.get(`${ENDPOINTS.USERS_ME}`);
    return response.data;
  };

  setOnboarded = async () => {
    await axiosInstance.post(`${ENDPOINTS.SET_ONBOARDED}`);
  };

  acceptCondition = async (): Promise<void> => {
    await axiosInstance.post(`${ENDPOINTS.ACCEPT_CONDITION}`);
  };

  createPushToken = async (pushToken: string): Promise<Article[]> => {
    const response = await axiosInstance.post(ENDPOINTS.CREATE_PUSH_TOKEN, null, {
      params: { pushToken },
    });
    return response.data;
  };

  saveUserLanguage = async (language: Language): Promise<OnboardedUser> => {
    const response = await axiosInstance.post(ENDPOINTS.SAVE_USER_LANGUAGE, null, {
      params: { language },
    });
    return response.data;
  };

  getAdminArticles = async (
    params: GetAdminArticlesParams,
  ): Promise<PaginatedResponse<AdminArticleRead>> => {
    const response = await axiosInstance.get(`${ENDPOINTS.ADMIN_ARTICLES}`, {
      params,
    });

    return response.data;
  };

  getAdminArticle = async (postId: number): Promise<AdminPostReadDetail> => {
    const response = await axiosInstance.get(`${ENDPOINTS.ADMIN_ARTICLES}/${postId}`);

    return response.data;
  };

  getAdminExtremeEvent = async (postId: number): Promise<AdminPostReadDetail> => {
    const response = await axiosInstance.get(`${ENDPOINTS.ADMIN_EXTREME_EVENTS}/${postId}`);

    return response.data;
  };

  getAdminEvent = async (postId: number): Promise<AdminPostReadDetail> => {
    const response = await axiosInstance.get(`${ENDPOINTS.ADMIN_EVENTS}/${postId}`);

    return response.data;
  };

  getBirthdayDetail = async (notificationId: number): Promise<BirthdayDetail> => {
    const response = await axiosInstance.get(`${ENDPOINTS.BIRTHDAY}/${notificationId}`);
    return response.data;
  };

  getBirthdayToday = async (): Promise<BirthdayDetail> => {
    const response = await axiosInstance.get(`${ENDPOINTS.BIRTHDAY_TODAY}`);
    return response.data;
  };

  createArticle = async (article: AdminArticleCreate): Promise<void> => {
    const response = await axiosInstance.post(`${ENDPOINTS.ADMIN_ARTICLES}`, article);

    return response.data;
  };

  updateArticle = async (postId: number, article: AdminArticleCreate): Promise<void> => {
    const response = await axiosInstance.patch(`${ENDPOINTS.ADMIN_ARTICLES}/${postId}`, article);

    return response.data;
  };

  createExtremeEvent = async (extremeEvent: AdminExtremeEventCreate): Promise<void> => {
    const response = await axiosInstance.post(`${ENDPOINTS.ADMIN_EXTREME_EVENTS}`, extremeEvent);

    return response.data;
  };

  updateExtremeEvent = async (
    postId: number,
    extremeEvent: AdminExtremeEventCreate,
  ): Promise<void> => {
    const response = await axiosInstance.patch(
      `${ENDPOINTS.ADMIN_EXTREME_EVENTS}/${postId}`,
      extremeEvent,
    );

    return response.data;
  };

  createEvent = async (event: AdminEventCreate): Promise<void> => {
    const response = await axiosInstance.post(`${ENDPOINTS.ADMIN_EVENTS}`, event);

    return response.data;
  };

  updateEvent = async (postId: number, event: AdminEventCreate): Promise<void> => {
    const response = await axiosInstance.patch(`${ENDPOINTS.ADMIN_EVENTS}/${postId}`, event);

    return response.data;
  };

  getAdminExtremeEvents = async (
    params: GetAdminExtremeEventsParams,
  ): Promise<PaginatedResponse<AdminExtremeEventRead>> => {
    const response = await axiosInstance.get(`${ENDPOINTS.ADMIN_EXTREME_EVENTS}`, {
      params,
    });

    return response.data;
  };

  getAdminEvents = async (
    params: GetAdminEventsParams,
  ): Promise<PaginatedResponse<AdminEventRead>> => {
    const response = await axiosInstance.get(`${ENDPOINTS.ADMIN_EVENTS}`, {
      params,
    });
    return response.data;
  };

  // HELPDESK TOPICS
  getAdminHelpdeskTopics = async (): Promise<AdminTopicRead[]> => {
    const response = await axiosInstance.get(`${ENDPOINTS.ADMIN_HELPDESK_TOPICS}`);
    return response.data;
  };

  createHelpdeskTopic = async (topic: AdminTopicCreate): Promise<void> => {
    const response = await axiosInstance.post(`${ENDPOINTS.ADMIN_HELPDESK_TOPICS}`, topic);
    return response.data;
  };

  searchAdminHelpdeskTopics = async (
    params: GetAdminHelpdeskCategoriesParams,
  ): Promise<PaginatedResponse<AdminTopicRead>> => {
    const { isActive, ...rest } = params;
    const response = await axiosInstance.get(`${ENDPOINTS.ADMIN_HELPDESK_TOPICS_SEARCH}`, {
      params: {
        ...rest,
        activityStatus: isActive ?? CategoriesStatusFilter.all,
      },
    });
    return response.data;
  };

  getAdminHelpdeskTopic = async (topicId: number): Promise<AdminTopicReadDetail> => {
    const response = await axiosInstance.get(`${ENDPOINTS.ADMIN_HELPDESK_TOPICS}/${topicId}`);

    return response.data;
  };

  updateHelpdeskTopic = async (topicId: number, topic: AdminTopicUpdate): Promise<void> => {
    const response = await axiosInstance.put(
      `${ENDPOINTS.ADMIN_HELPDESK_TOPICS}/${topicId}`,
      topic,
    );
    return response.data;
  };

  patchHelpdeskTopicActivity = async (
    topicId: number,
    topicActivity: TopicActivity,
  ): Promise<void> => {
    const response = await axiosInstance.patch(
      `${ENDPOINTS.ADMIN_HELPDESK_TOPICS_ACTIVITY}/${topicId}`,
      topicActivity,
    );
    return response.data;
  };

  // HELPDESK ORGANIZATIONS
  getAdminHelpdeskOrganizations = async (): Promise<AdminOrganizationRead[]> => {
    const response = await axiosInstance.get(`${ENDPOINTS.ADMIN_HELPDESK_ORGANIZATIONS}`);
    return response.data;
  };

  searchAdminHelpdeskOrganizations = async (
    params: GetAdminHelpdeskCategoriesParams,
  ): Promise<PaginatedResponse<AdminOrganizationRead>> => {
    const { isActive, ...rest } = params;
    const response = await axiosInstance.get(`${ENDPOINTS.ADMIN_HELPDESK_ORGANIZATIONS_SEARCH}`, {
      params: {
        ...rest,
        activityStatus: isActive ?? CategoriesStatusFilter.all,
      },
    });
    return response.data;
  };

  createHelpdeskOrganization = async (organization: AdminOrganizationCreate): Promise<void> => {
    const response = await axiosInstance.post(
      `${ENDPOINTS.ADMIN_HELPDESK_ORGANIZATIONS}`,
      organization,
    );
    return response.data;
  };

  getAdminHelpdeskOrganization = async (
    organizationId: number,
  ): Promise<AdminOrganizationReadDetail> => {
    const response = await axiosInstance.get(
      `${ENDPOINTS.ADMIN_HELPDESK_ORGANIZATIONS}/${organizationId}`,
    );
    return response.data;
  };

  updateHelpdeskOrganization = async (
    organizationId: number,
    organization: AdminOrganizationUpdate,
  ): Promise<void> => {
    const response = await axiosInstance.put(
      `${ENDPOINTS.ADMIN_HELPDESK_ORGANIZATIONS}/${organizationId}`,
      organization,
    );
    return response.data;
  };

  patchHelpdeskOrganizationActivity = async (
    organizationId: number,
    isActive: boolean,
  ): Promise<void> => {
    const response = await axiosInstance.patch(
      `${ENDPOINTS.ADMIN_HELPDESK_ORGANIZATIONS_ACTIVITY}/${organizationId}`,
      { isActive },
    );
    return response.data;
  };

  // HELPDESK ISSUES
  getAdminPaginatedHelpdeskIssues = async (
    params: GetAdminHelpdeskIssuesParams,
  ): Promise<PaginatedResponse<AdminPostRead>> => {
    const { topics, organizations, status, ...rest } = params;
    const response = await axiosInstance.get(`${ENDPOINTS.ADMIN_HELPDESK_ISSUES}`, {
      params: {
        ...rest,
        topicIdList: topics?.map((t) => t.id),
        organizationIdList: organizations?.map((o) => o.id),
        status: status === HelpdeskIssueStatusFilter.all ? undefined : status,
      },
    });
    return response.data;
  };

  createAdminHelpdeskIssues = async (
    helpdeskIssue: AdminHelpdeskIssueCreate,
  ): Promise<{ id: number }> => {
    const response = await axiosInstance.post(`${ENDPOINTS.ADMIN_HELPDESK_ISSUES}`, helpdeskIssue);
    return response.data;
  };

  getAdminHelpdeskIssue = async (postId: number): Promise<AdminHelpdeskIssueRead> => {
    const response = await axiosInstance.get(`${ENDPOINTS.ADMIN_HELPDESK_ISSUES}/${postId}`);
    return response.data;
  };

  updateAdminHelpdeskIssue = async (
    postId: number,
    helpdeskIssue: AdminHelpdeskIssueCreate,
  ): Promise<void> => {
    const response = await axiosInstance.put(
      `${ENDPOINTS.ADMIN_HELPDESK_ISSUES}/${postId}`,
      helpdeskIssue,
    );
    return response.data;
  };

  getCalendarEvents = async (): Promise<CalendarEvent[]> => {
    const response = await axiosInstance.get(`${ENDPOINTS.CALENDAR_EVENTS}`);
    return response.data;
  };

  deletePost = async (postId: number): Promise<void> => {
    await axiosInstance.delete(`${ENDPOINTS.ADMIN_POSTS}/${postId}`);
  };

  getFileUploadUrl = async (
    module: FileModuleType,
    controller: AbortController,
  ): Promise<UploadFileResponse> => {
    const response = await axiosInstance.post(`${ENDPOINTS.ADMIN_FILES}`, null, {
      params: { module },
      signal: controller.signal,
    });
    return response.data;
  };

  deleteFile = async (fileId: number): Promise<void> => {
    await axiosInstance.delete(`${ENDPOINTS.ADMIN_FILES}/${fileId}`);
  };

  getActiveHelpdeskTopics = async (): Promise<Topic[]> => {
    const response = await axiosInstance.get(ENDPOINTS.HELPDESK_TOPICS_ACTIVE);
    return response.data.map((t: { id: number; name: string; is_active: boolean }) => ({
      id: t.id,
      name: t.name,
      translationValue: t.name,
      isActive: t.is_active,
    }));
  };

  getActiveHelpdeskOrganizations = async (): Promise<Organization[]> => {
    const response = await axiosInstance.get(ENDPOINTS.HELPDESK_ORGANIZATIONS_ACTIVE);
    return response.data.map((o: { id: number; name: string; is_active: boolean }) => ({
      id: o.id,
      name: o.name,
      isActive: o.is_active,
    }));
  };

  getHelpdeskIssues = async (
    params?: GetHelpdeskIssuesSearchParams,
  ): Promise<HelpdeskIssueSearchResult[]> => {
    const response = await axiosInstance.get(ENDPOINTS.HELPDESK_ISSUES_SEARCH, {
      params: {
        search: params?.search,
        topicIdList: params?.topicIdList,
        organizationIdList: params?.organizationIdList,
      },
    });
    return response.data;
  };
}

export default new Client();
